import { DateTime } from "luxon";
import { capitalizeFirstLetter } from "@src/lib/utils/strings";
import { CommsOptInLaunchType, Timezone } from "@src/types.generated";

export type LaunchPick = {
  launchType: CommsOptInLaunchType | null;
  launchAt: string | null;
};

export const PAST_LAUNCH_TIME_ERROR = "Pick a launch time in the future";

export const IANA_TIME_ZONES: Record<Timezone, string> = {
  [Timezone.UsEastern]: "America/New_York",
  [Timezone.UsCentral]: "America/Chicago",
  [Timezone.UsMountain]: "America/Denver",
  [Timezone.UsPacific]: "America/Los_Angeles",
  [Timezone.UsArizona]: "America/Phoenix",
};

const NEARBY_DAYS = ["today", "tomorrow"];

export const formatLaunchAt = (
  launchAt: string,
  timeZone: Timezone,
  // absolute: always the date, never "today" or "tomorrow", for a sentence
  // that reads "on {date}".
  { midSentence = false, absolute = false } = {},
): string => {
  const at = DateTime.fromISO(launchAt, { zone: IANA_TIME_ZONES[timeZone] });
  const day = at.toRelativeCalendar({
    base: DateTime.now().setZone(at.zone),
    locale: "en",
    unit: "days",
  });
  if (absolute || !day || !NEARBY_DAYS.includes(day)) {
    // The year only when it isn't this one, so a date months out can't be
    // read as this year's.
    return at.hasSame(DateTime.now().setZone(at.zone), "year")
      ? at.toFormat("ccc LLL d h:mm a ZZZZ")
      : at.toFormat("ccc LLL d, yyyy h:mm a ZZZZ");
  }
  return `${midSentence ? day : capitalizeFirstLetter(day)} at ${at.toFormat("h:mm a ZZZZ")}`;
};

export const isLaunchPicked = (pick: LaunchPick): boolean =>
  pick.launchType === CommsOptInLaunchType.Now ||
  (pick.launchType === CommsOptInLaunchType.Date && !!pick.launchAt);
